import { fromStripeSmallestUnit } from "../utils/stripe-price.util";

export type SubscriptionPackageId = "month" | "year";

export interface SubscriptionPackageFeature {
  text: string;
  icon: string;
}

export interface SubscriptionPackage {
  id: SubscriptionPackageId;
  name: string;
  price: number;
  currency: string;
  periodLabel: string;
  description: string;
  descriptionIcon: string;
  features: SubscriptionPackageFeature[];
  recommended?: boolean;
}

export const SUBSCRIPTION_PACKAGES: SubscriptionPackage[] = [
  {
    id: "month",
    name: "Monthly",
    price: 9.99,
    currency: "USD",
    periodLabel: "per month",
    description: "Extend safe access for 1 month.",
    descriptionIcon: "assets/icons/li_clock.svg",
    features: [
      { text: "Full preview access", icon: "assets/icons/li_eye.svg" },
      { text: "Shareable link restored", icon: "assets/icons/li_share.svg" },
      { text: "Cancel anytime", icon: "assets/icons/li_log-out.svg" },
    ],
  },
  {
    id: "year",
    name: "Yearly",
    price: 89.99,
    currency: "USD",
    periodLabel: "per year",
    description: "Best value — extend safe access for 12 months.",
    descriptionIcon: "assets/icons/li_check.svg",
    features: [
      { text: "Full preview access", icon: "assets/icons/li_eye.svg" },
      { text: "Shareable link restored", icon: "assets/icons/li_share.svg" },
    ],
    recommended: true,
  },
];

export const DEFAULT_RENEWAL_PRICING = {
  renewalMonthlyPrice: 9.99,
  renewalYearlyPrice: 89.99,
  renewalCurrency: "USD",
};

export interface PaymentSettingPricing {
  monthlyPrice?: number | string | null;
  annualPrice?: number | string | null;
  currency?: string | null;
}

export interface RenewalPricingSource {
  renewalMonthlyPrice?: number | string | null;
  renewalYearlyPrice?: number | string | null;
  renewalCurrency?: string | null;
  paymentSetting?: PaymentSettingPricing | null;
}

export function hasPaymentSettingPricing(pricingSource?: RenewalPricingSource): boolean {
  const paymentSetting = pricingSource && pricingSource.paymentSetting;
  if (!paymentSetting) {
    return false;
  }

  const monthly = Number(paymentSetting.monthlyPrice);
  const annual = Number(paymentSetting.annualPrice);
  return (
    (Number.isFinite(monthly) && monthly > 0) || (Number.isFinite(annual) && annual > 0)
  );
}

export function getSubscriptionPackage(
  id: SubscriptionPackageId,
  pricingSource?: RenewalPricingSource
): SubscriptionPackage | undefined {
  if (!hasPaymentSettingPricing(pricingSource)) {
    return undefined;
  }

  const packages = buildSubscriptionPackages(pricingSource);
  return packages.find(pkg => pkg.id === id);
}

export function buildSubscriptionPackages(pricingSource?: RenewalPricingSource): SubscriptionPackage[] {
  if (!hasPaymentSettingPricing(pricingSource)) {
    return [];
  }

  const resolved = resolveRenewalPricing(pricingSource);
  const savePercent = calculateAnnualSavingsPercent(resolved.monthlyPrice, resolved.yearlyPrice);

  return SUBSCRIPTION_PACKAGES.map(pkg => {
    if (pkg.id === "month") {
      return { ...pkg, price: resolved.monthlyPrice, currency: resolved.currency };
    }

    if (pkg.id === "year") {
      return {
        ...pkg,
        price: resolved.yearlyPrice,
        currency: resolved.currency,
        features: buildYearlyFeatures(pkg.features, savePercent),
      };
    }

    return { ...pkg };
  });
}

/**
 * Savings vs paying monthly for 12 months:
 * ((monthly * 12 - yearly) / (monthly * 12)) * 100
 * Returns 0 when there is no positive savings.
 */
export function calculateAnnualSavingsPercent(monthlyPrice: number, yearlyPrice: number): number {
  const monthly = Number(monthlyPrice);
  const yearly = Number(yearlyPrice);
  if (!Number.isFinite(monthly) || !Number.isFinite(yearly) || monthly <= 0) {
    return 0;
  }

  const monthlyYearCost = monthly * 12;
  if (monthlyYearCost <= 0 || yearly >= monthlyYearCost) {
    return 0;
  }

  return Math.round(((monthlyYearCost - yearly) / monthlyYearCost) * 100);
}

function buildYearlyFeatures(
  baseFeatures: SubscriptionPackageFeature[],
  savePercent: number
): SubscriptionPackageFeature[] {
  const features = (baseFeatures || [])
    .filter((feature) => !/^Save\s+\d+%\s+vs monthly$/i.test(feature.text))
    .map((feature) => ({ ...feature }));

  if (savePercent > 0) {
    features.push({
      text: "Save " + savePercent + "% vs monthly",
      icon: "assets/icons/li_check.svg",
    });
  }

  return features;
}

/** Map DML detail / preview payload (including nested paymentSetting in cents) to display prices. */
export function resolveRenewalPricing(
  pricingSource?: RenewalPricingSource
): { monthlyPrice: number; yearlyPrice: number; currency: string } {
  const paymentSetting = pricingSource && pricingSource.paymentSetting;

  if (paymentSetting) {
    const currencyFromSetting =
      paymentSetting.currency && String(paymentSetting.currency).trim()
        ? String(paymentSetting.currency).trim().toUpperCase()
        : DEFAULT_RENEWAL_PRICING.renewalCurrency;
    const monthlyFromCents = fromSmallestUnit(paymentSetting.monthlyPrice, currencyFromSetting);
    const yearlyFromCents = fromSmallestUnit(paymentSetting.annualPrice, currencyFromSetting);

    return {
      monthlyPrice:
        monthlyFromCents != null && monthlyFromCents > 0
          ? monthlyFromCents
          : DEFAULT_RENEWAL_PRICING.renewalMonthlyPrice,
      yearlyPrice:
        yearlyFromCents != null && yearlyFromCents > 0
          ? yearlyFromCents
          : DEFAULT_RENEWAL_PRICING.renewalYearlyPrice,
      currency: currencyFromSetting,
    };
  }

  return {
    monthlyPrice: parseRenewalPrice(
      pricingSource && pricingSource.renewalMonthlyPrice,
      DEFAULT_RENEWAL_PRICING.renewalMonthlyPrice
    ),
    yearlyPrice: parseRenewalPrice(
      pricingSource && pricingSource.renewalYearlyPrice,
      DEFAULT_RENEWAL_PRICING.renewalYearlyPrice
    ),
    currency:
      pricingSource && pricingSource.renewalCurrency && String(pricingSource.renewalCurrency).trim()
        ? String(pricingSource.renewalCurrency).trim().toUpperCase()
        : DEFAULT_RENEWAL_PRICING.renewalCurrency,
  };
}

function fromSmallestUnit(
  amount: number | string | null | undefined,
  currency?: string
): number | null {
  return fromStripeSmallestUnit(amount, currency);
}

function parseRenewalPrice(value: number | string | null | undefined, fallback: number): number {
  if (value == null || value === "") {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}
